import { useId, useState, useRef, useEffect, useCallback, type CSSProperties } from 'react';
import { useAnimationFrame } from '../viz/useAnimationFrame';
import {
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../viz/vizShared';

// UV-VIS spectrophotometer. Shows the optical path (source → monochromator slit →
// cuvette → detector), a digital absorbance / %transmittance readout, and a small
// Beer's-law plot (A vs concentration) with the current sample marked. Instrument
// body colors are physical; brand accent + status LED use --ws-* tokens.
//
// CUE CONTRACT (optional, additive): cue.progress (0..1) drives sample
// concentration → absorbance via Beer's law (A = ε·b·c), with the beam dimming as
// it passes the cuvette; cue.play=true animates the light beam; a numeric
// highlight like '540' or 'nm:600' sets the wavelength. With no cue it's
// interactive (click the sample slot to insert/remove a cuvette).

export interface SpectrophotometerProps {
  wavelength?: number; // nm (200-800)
  absorbance?: number; // A (0-3)
  sampleIn?: boolean;
  onInsertSample?: () => void;
  disabled?: boolean;
  className?: string;
  /** Sample concentration (relative units) for the Beer's-law plot. */
  concentration?: number;
  /** Narration-sync cue forwarded by the lecture player. */
  cue?: VizCue;
}

// Map a visible wavelength (nm) to an approximate RGB colour for the beam.
function wavelengthColor(nm: number): string {
  const w = Math.max(380, Math.min(780, nm));
  let r = 0, g = 0, b = 0;
  if (w < 440) { r = -(w - 440) / 60; b = 1; }
  else if (w < 490) { g = (w - 440) / 50; b = 1; }
  else if (w < 510) { g = 1; b = -(w - 510) / 20; }
  else if (w < 580) { r = (w - 510) / 70; g = 1; }
  else if (w < 645) { r = 1; g = -(w - 645) / 65; }
  else { r = 1; }
  const f = w > 700 ? 0.3 + (0.7 * (780 - w)) / 80 : w < 420 ? 0.3 + (0.7 * (w - 380)) / 40 : 1;
  const ch = (v: number) => Math.round(255 * Math.max(0, Math.min(1, v)) * f);
  return `rgb(${ch(r)},${ch(g)},${ch(b)})`;
}

export default function Spectrophotometer({
  wavelength = 540,
  absorbance = 0.0,
  sampleIn = false,
  onInsertSample,
  disabled = false,
  className = '',
  concentration,
  cue,
}: SpectrophotometerProps) {
  const id = useId();
  const reduced = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);
  const tokens = cueHighlights(cue);
  const [isHovered, setIsHovered] = useState(false);

  // Wavelength from a numeric highlight (200..800) if present.
  const wlToken = tokens
    .map((t) => {
      const m = t.match(/(\d{3,4})/);
      return m ? parseInt(m[1], 10) : NaN;
    })
    .find((n) => !Number.isNaN(n) && n >= 200 && n <= 1000);
  const wl = wlToken ?? wavelength;

  // Sample insertion: own it for interaction / cue.
  const [inserted, setInserted] = useState(sampleIn);
  useEffect(() => setInserted(sampleIn), [sampleIn]);
  const cueDrive = cueOn && (cue?.play === true || cue?.progress != null);
  const sampleActive = cueDrive ? true : inserted;

  // Beer's law: A = ε·b·c. With cue.progress we sweep concentration; otherwise
  // we use the provided absorbance (or concentration) directly.
  const EPS_B = 2.4; // ε·b for the demo so c∈[0,1] → A∈[0,2.4]
  const conc = cue?.progress != null ? cue.progress : concentration;
  const targetA = sampleActive
    ? conc != null
      ? conc * EPS_B
      : absorbance
    : 0;

  // Ease the displayed absorbance for a meter-stabilising feel.
  const aRef = useRef(0);
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => (n + 1) % 1_000_000), []);
  const beamPhase = useRef(0);
  useAnimationFrame((dt) => {
    aRef.current += (targetA - aRef.current) * Math.min(1, dt * 5);
    if (Math.abs(aRef.current - targetA) < 0.001) aRef.current = targetA;
    beamPhase.current += dt;
    bump();
  }, !reduced);
  const A = reduced ? targetA : aRef.current;
  const transmittance = Math.pow(10, -A) * 100; // %T
  const beamColor = wavelengthColor(wl);

  // Beam intensity after the cuvette ∝ %T.
  const exitOpacity = sampleActive ? Math.max(0.06, Math.pow(10, -A)) : 1;

  const containerStyle: CSSProperties = { opacity: disabled ? 0.5 : 1, transition: 'all 0.2s ease' };
  const sampleHolderStyle: CSSProperties = {
    cursor: disabled ? 'default' : onInsertSample || true ? 'pointer' : 'default',
  };

  const toggleSample = () => {
    if (disabled) return;
    onInsertSample?.();
    setInserted((v) => !v);
  };

  // --- Beer's-law mini plot geometry (inside display panel). Domain c∈[0,1].
  const plot = { x: 122, y: 50, w: 80, h: 46 };
  const currentC = conc != null ? conc : sampleActive ? Math.min(1, absorbance / EPS_B) : 0;
  const px = plot.x + currentC * plot.w;
  const py = plot.y + plot.h - (Math.min(targetA, EPS_B) / EPS_B) * plot.h;

  // dashed flowing beam offset
  const dash = reduced ? 0 : (beamPhase.current * 40) % 12;

  return (
    <svg
      viewBox="0 0 220 150"
      width={300}
      height={205}
      style={containerStyle}
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="img"
      aria-label={`Spectrophotometer, ${wl} nm, absorbance ${A.toFixed(3)}`}
    >
      <defs>
        <linearGradient id={`body-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#3A3A3A" />
          <stop offset="50%" stopColor="#2C2C2C" />
          <stop offset="100%" stopColor="#1A1A1A" />
        </linearGradient>
        <linearGradient id={`side-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2A2A2A" />
          <stop offset="100%" stopColor="#1A1A1A" />
        </linearGradient>
        <filter id={`display-glow-${id}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={`shadow-${id}`} x="-5%" y="-5%" width="115%" height="120%">
          <feDropShadow dx="3" dy="4" stdDeviation="3" floodOpacity="0.4" />
        </filter>
        <linearGradient id={`cuvette-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
          <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
          <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
        </linearGradient>
        <filter id={`beam-glow-${id}`} x="-30%" y="-200%" width="160%" height="500%">
          <feGaussianBlur stdDeviation="1.4" />
        </filter>
      </defs>

      <g filter={`url(#shadow-${id})`}>
        <rect x="8" y="22" width="204" height="100" fill={`url(#body-${id})`} rx="8" />
        <rect x="8" y="22" width="204" height="14" fill="#3D3D3D" rx="8" />
        <rect x="8" y="30" width="204" height="8" fill="#3D3D3D" />
        <rect x="8" y="22" width="8" height="100" fill={`url(#side-${id})`} rx="8" />

        <rect x="18" y="27" width="48" height="8" fill="var(--ws-accent)" rx="2" />
        <text x="42" y="33.4" fontSize="6" fill="white" textAnchor="middle" fontWeight="bold" fontFamily="Arial, sans-serif">
          UV-VIS
        </text>

        {/* ───────── Optical path: source → slit → cuvette → detector ───────── */}
        {/* Lamp source. */}
        <circle cx="20" cy="92" r="6" fill="#FFF3C4" stroke="#9A8A4A" strokeWidth="1" />
        <circle cx="20" cy="92" r="3" fill={beamColor}>
          {!reduced && <animate attributeName="opacity" values="1;0.6;1" dur="1.5s" repeatCount="indefinite" />}
        </circle>
        <text x="20" y="106" fontSize="4.5" fill="#888" textAnchor="middle">SOURCE</text>

        {/* Monochromator slit. */}
        <rect x="34" y="84" width="4" height="16" fill="#0A0A0A" />
        <rect x="35.2" y="90" width="1.6" height="4" fill={beamColor} />

        {/* Beam: source → cuvette (full intensity). */}
        <line x1="26" y1="92" x2="74" y2="92" stroke={beamColor} strokeWidth="2.4" strokeLinecap="round" strokeDasharray="6 6" strokeDashoffset={-dash} filter={`url(#beam-glow-${id})`} opacity="0.95" />

        {/* Cuvette + sample. */}
        <g style={sampleHolderStyle} onClick={toggleSample}>
          <rect x="70" y="78" width="20" height="34" fill="#0A0A0A" stroke={isHovered ? 'var(--ws-accent)' : '#3A3A3A'} strokeWidth="1" rx="2" />
          {sampleActive ? (
            <g>
              <rect x="73" y="80" width="14" height="30" fill={`url(#cuvette-${id})`} stroke="#7A9BB8" strokeWidth="0.5" rx="1" />
              {/* Solution colour deepens with absorbance. */}
              <rect x="75" y="84" width="10" height="24" fill={beamColor} opacity={Math.min(0.85, 0.18 + A * 0.32)} rx="1" />
              <line x1="75" y1="82" x2="75" y2="106" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
            </g>
          ) : (
            <text x="80" y="98" fontSize="14" fill="#3A3A3A" textAnchor="middle">+</text>
          )}
        </g>

        {/* Beam: cuvette → detector (attenuated by %T). */}
        <line x1="90" y1="92" x2="104" y2="92" stroke={beamColor} strokeWidth="2.4" strokeLinecap="round" strokeDasharray="6 6" strokeDashoffset={-dash} filter={`url(#beam-glow-${id})`} opacity={exitOpacity} />

        {/* Detector / photodiode. */}
        <rect x="104" y="84" width="8" height="16" fill="#1A1A1A" stroke="#4A4A4A" strokeWidth="0.8" rx="1" />
        <rect x="105.5" y="88" width="5" height="8" fill={sampleActive ? beamColor : '#222'} opacity={exitOpacity} rx="0.5" />
        <text x="108" y="106" fontSize="4.5" fill="#888" textAnchor="middle">DET</text>

        {/* ───────── Digital readout panel ───────── */}
        <rect x="120" y="42" width="86" height="58" fill="#1A1A1A" stroke="#4A4A4A" strokeWidth="1.5" rx="4" />

        {/* Beer's-law plot. */}
        <rect x={plot.x} y={plot.y} width={plot.w} height={plot.h} fill="#001a00" rx="2" />
        {/* axes */}
        <line x1={plot.x} y1={plot.y + plot.h} x2={plot.x + plot.w} y2={plot.y + plot.h} stroke="#0A5A2A" strokeWidth="0.6" />
        <line x1={plot.x} y1={plot.y} x2={plot.x} y2={plot.y + plot.h} stroke="#0A5A2A" strokeWidth="0.6" />
        {/* Beer's law line A = ε·b·c (straight through origin). */}
        <line x1={plot.x} y1={plot.y + plot.h} x2={plot.x + plot.w} y2={plot.y} stroke="#00A86B" strokeWidth="0.8" opacity="0.7" strokeDasharray="2 2" />
        {/* current sample marker */}
        {(sampleActive || currentC > 0) && (
          <g>
            <line x1={px} y1={plot.y + plot.h} x2={px} y2={py} stroke="#00E676" strokeWidth="0.5" opacity="0.5" />
            <line x1={plot.x} y1={py} x2={px} y2={py} stroke="#00E676" strokeWidth="0.5" opacity="0.5" />
            <circle cx={px} cy={py} r="2" fill={beamColor} stroke="#00E676" strokeWidth="0.8" />
          </g>
        )}
        <text x={plot.x + plot.w} y={plot.y + plot.h + 4.5} fontSize="4" fill="#0A8A4A" textAnchor="end" fontFamily="Arial">conc →</text>
        <text x={plot.x - 1.5} y={plot.y - 1.5} fontSize="4" fill="#0A8A4A" textAnchor="start" fontFamily="Arial">A</text>

        {/* Numeric readouts. */}
        <g filter={`url(#display-glow-${id})`} fontFamily="'Courier New', monospace" fontWeight="bold">
          <text x="124" y="56" fontSize="11" fill="#00E676">A {A.toFixed(3)}</text>
          <text x="124" y="68" fontSize="7" fill="#00A86B">%T {transmittance.toFixed(1)}</text>
        </g>

        {/* Wavelength dial. */}
        <text x="124" y="80" fontSize="6.5" fill="#888" fontFamily="Arial">λ</text>
        <rect x="131" y="74" width="34" height="9" fill="#0A0A0A" rx="2" />
        <text x="148" y="80.5" fontSize="7" fill={beamColor} textAnchor="middle" fontFamily="'Courier New', monospace" fontWeight="bold" filter={`url(#display-glow-${id})`}>
          {wl} nm
        </text>
        <circle cx="178" cy="78" r="9" fill="#3A3A3A" stroke="#5A5A5A" strokeWidth="1.5" />
        <circle cx="178" cy="78" r="6.5" fill="#2A2A2A" />
        <line x1="178" y1="78" x2="178" y2="71.5" stroke="var(--ws-accent)" strokeWidth="1.6" strokeLinecap="round" transform={`rotate(${(wl - 200) / 600 * 270 - 135} 178 78)`} />

        {/* Buttons. */}
        <rect x="122" y="88" width="20" height="9" fill="#4A4A4A" rx="2" />
        <text x="132" y="94.4" fontSize="5" fill="#E0E0E0" textAnchor="middle">ZERO</text>
        <rect x="145" y="88" width="20" height="9" fill="#4A4A4A" rx="2" />
        <text x="155" y="94.4" fontSize="5" fill="#E0E0E0" textAnchor="middle">MODE</text>
        <rect x="168" y="88" width="36" height="9" fill="#00A86B" rx="2" />
        <text x="186" y="94.4" fontSize="5" fill="white" textAnchor="middle">READ</text>

        {/* Status LED. */}
        <circle cx="200" cy="32" r="3.4" fill={sampleActive ? '#00E676' : 'var(--ws-accent)'}>
          {!reduced && <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />}
        </circle>

        {/* Vents. */}
        {[0, 1, 2, 3, 4].map((i) => (
          <rect key={i} x={18 + i * 7} y="114" width="4" height="2" fill="#0A0A0A" rx="0.5" />
        ))}
      </g>

      <text x="110" y="142" fontSize="10" fill="var(--ws-ink-2)" textAnchor="middle" fontWeight="500">
        Spectrophotometer
      </text>
    </svg>
  );
}
